import React, { useEffect, useState } from "react";
import PropTypes from 'prop-types';
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import { Box, Typography } from "@mui/material";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix for Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
    iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const DEFAULT_COORDS = { lat: 51.505, lng: -0.09 };
const ZOOM_STYLE_ID = 'map-zoom-style';
const ZOOM_STYLE = `
    .leaflet-control-zoom a, .leaflet-bar a {
        background: #fff !important;
        color: #000 !important;
        border: 1px solid rgba(0,0,0,0.08) !important;
        box-shadow: none !important;
        width: 34px !important;
        height: 34px !important;
        line-height: 34px !important;
        text-align: center !important;
        font-weight: 700 !important;
        border-radius: 4px !important;
    }
    .leaflet-control-zoom a.leaflet-control-zoom-in,
    .leaflet-control-zoom a.leaflet-control-zoom-out {
        padding: 0 !important;
    }
`;

const MapField = ({ coordinates, label }) => {
    const [markerPosition, setMarkerPosition] = useState(coordinates ?? DEFAULT_COORDS);
    const [markerSet, setMarkerSet] = useState(false);

    // Handle clicks on the map to set marker once
    const LocationMarker = () => {
        useMapEvents({
            click: (e) => {
                if (!markerSet) {
                    const newCoordinates = { lat: e.latlng?.lat, lng: e.latlng?.lng };
                    setMarkerPosition(newCoordinates);
                    setMarkerSet(true);
                }
            },
        });
        return markerPosition ? <Marker position={markerPosition} /> : null;
    };

    // Sync incoming coordinates
    useEffect(() => {
        if (coordinates) setMarkerPosition(coordinates);
    }, [coordinates]);

    // Inject zoom-control styles once (guard to avoid duplicates)
    useEffect(() => {
        if (document.head.querySelector(`[data-injected="${ZOOM_STYLE_ID}"]`)) return;
        const style = document.createElement('style');
        style.setAttribute('data-injected', ZOOM_STYLE_ID);
        style.innerHTML = ZOOM_STYLE;
        document.head.appendChild(style);
        return () => {
            const existing = document.head.querySelector(`[data-injected="${ZOOM_STYLE_ID}"]`);
            if (existing) existing.remove();
        };
    }, []);

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {label || "Select Location"}
            </Typography>

            <MapContainer
                key={`${markerPosition?.lat}-${markerPosition?.lng}`}
                center={markerPosition}
                zoom={13}
                style={{ height: "400px", width: "100%", marginBottom: "16px" }}
            >
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                <LocationMarker />
            </MapContainer>
        </Box>
    );
};

MapField.propTypes = {
    coordinates: PropTypes.shape({
        lat: PropTypes.number,
        lng: PropTypes.number,
    }),
    label: PropTypes.string,
};

MapField.defaultProps = {
    coordinates: DEFAULT_COORDS,
    label: 'Select Location',
};

export default MapField;
